/** What the schedule's list, calendar and detail share: how a feed item is
 *  named, what it can do, the confirm-then-act commands, the slot input and
 *  the "write a post" dialog. */

import { useState, type ReactNode } from 'react'
import { useLocation, useNavigate, type Location } from 'react-router-dom'
import { isManualPlatform, type ScheduleItem } from '@/api/client'
import { usePostActions, usePublishingRefs, type PostRef } from '@/lib/postActions'
import { fromLocalInput, pastSlotError, toLocalInput } from '@/lib/time'
import { Button, ConfirmDialog, Dialog, Segmented, SlotPicker, errorText, platformName, useToast, type Source } from '@/ui'

// ── naming ─────────────────────────────────────────────────────────────────

export const sourceOf = (it: Pick<ScheduleItem, 'kind'>): Source => (it.kind === 'redeem' ? 'redeem' : 'campaign')

export const refOf = (it: Pick<ScheduleItem, 'kind' | 'id'>): PostRef => ({ kind: it.kind, id: it.id })

/** The item's place on the timeline: when it went out, else its slot. */
export function itemTime(it: Pick<ScheduleItem, 'status' | 'scheduled_at' | 'published_at'>): string | null {
  return it.status === 'published' && it.published_at ? it.published_at : it.scheduled_at
}

/** A desk post on a channel the operator posts to by hand. */
export const isManualItem = (it: Pick<ScheduleItem, 'kind' | 'platform'>) =>
  it.kind === 'redeem' && isManualPlatform(it.platform)

/** Where it goes, in a few characters: r/sub, the account, the channel note. */
export function channelLabel(it: Pick<ScheduleItem, 'kind' | 'platform' | 'channel' | 'target'>): string {
  if (it.platform === 'reddit') return it.channel ? `r/${it.channel}` : 'Reddit'
  if (isManualItem(it)) return it.channel ? `${platformName(it.platform)} · ${it.channel}` : platformName(it.platform)
  if (it.target?.name) return `@${it.target.name}`
  return platformName(it.platform)
}

/** The app or campaign the post belongs to. */
export const ownerName = (it: Pick<ScheduleItem, 'app' | 'campaign'>) => it.app?.name ?? it.campaign?.name ?? ''

/** The title, or the body's first line for a post without one (Threads). */
export function headline(it: Pick<ScheduleItem, 'title' | 'excerpt'>, max = 60): string {
  if (it.title.trim()) return it.title
  const first = it.excerpt.split('\n').find((l) => l.trim()) ?? ''
  return first.length > max ? `${first.slice(0, max)}…` : first || '(본문 없음)'
}

/** "r/AppGiveaway", "Threads @noveldesk.app", "Bluesky" — the confirm text's "where". */
export function whereLabel(it: Pick<ScheduleItem, 'kind' | 'platform' | 'channel' | 'target'>): string {
  if (it.platform === 'reddit') return `r/${it.channel}`
  const name = platformName(it.platform)
  return it.target?.name ? `${name} @${it.target.name}` : name
}

export const detailPath = (ref: PostRef) => (ref.kind === 'redeem' ? `/redeem/posts/${ref.id}` : `/drafts/${ref.id}`)

/** Waiting for a slot: an approved draft without one, a desk draft. */
export function isWaiting(it: Pick<ScheduleItem, 'kind' | 'status' | 'scheduled_at' | 'platform'>): boolean {
  if (it.kind === 'draft') return it.status === 'approved' && !it.scheduled_at
  return it.status === 'draft' && !isManualPlatform(it.platform)
}

/** Minutes a publish has been under way, or null when it is not. */
export function publishingFor(it: Pick<ScheduleItem, 'status' | 'updated_at'>, now = new Date()): number | null {
  if (it.status !== 'publishing') return null
  const t = new Date(it.updated_at).getTime()
  return Number.isNaN(t) ? null : Math.floor((now.getTime() - t) / 60_000)
}

/** A publish under way this long has likely died. */
export const STUCK_MINUTES = 10

/** What an item can do from a list row or the calendar. Manual channels are
 *  posted by hand: no slot, no publish — they open the detail instead. */
export function abilities(it: Pick<ScheduleItem, 'kind' | 'status' | 'platform' | 'scheduled_at'>) {
  const manual = isManualItem(it)
  const s = it.status
  const slotable =
    !manual && (it.kind === 'redeem' ? ['draft', 'scheduled', 'failed'] : ['approved', 'scheduled', 'failed']).includes(s)
  return {
    /** Set or move the slot. */
    schedule: slotable,
    unschedule:
      !manual &&
      (it.kind === 'redeem' ? s === 'scheduled' || s === 'failed' : ['approved', 'scheduled', 'failed'].includes(s)),
    publishNow: slotable,
    /** Moving it is "시각 변경"; a first slot is "예약"; after a failure "다시 예약". */
    scheduleLabel: s === 'failed' ? '다시 예약' : s === 'scheduled' || (s === 'approved' && it.scheduled_at) ? '시각 변경' : '예약',
  }
}

// ── opening a detail ───────────────────────────────────────────────────────

export interface DetailState {
  /** The page the detail was opened from; it stays drawn under the drawer. */
  background?: Location
}

/** Opens a post's detail over the current page. The URL changes, so a reload
 *  or a shared link lands on the same post. */
export function useOpenDetail() {
  const navigate = useNavigate()
  const location = useLocation()
  return (ref: PostRef) => {
    const bg = (location.state as DetailState | null)?.background ?? location
    navigate(detailPath(ref), { state: { background: bg } satisfies DetailState })
  }
}

// ── commands that ask first ────────────────────────────────────────────────

interface Pending {
  type: 'publish' | 'unschedule'
  ref: PostRef
  where: string
}

/** Publish-now and take-off-the-schedule, each behind a ConfirmDialog; and a
 *  slot set, which needs no question. Results go to a toast. */
export function usePostCommands(opts: { onError?: (e: unknown) => void } = {}) {
  const actions = usePostActions()
  const toast = useToast()
  const [pending, setPending] = useState<Pending | null>(null)
  const [busy, setBusy] = useState(false)
  // A publish already on its way, from this screen or another: pressing it a
  // second time would send the same post again.
  const publishing = usePublishingRefs()
  const isPublishing = (ref: PostRef) => publishing.some((p) => p.kind === ref.kind && p.id === ref.id)

  const confirm = async () => {
    if (!pending) return
    if (pending.type === 'publish' && isPublishing(pending.ref)) return
    setBusy(true)
    try {
      if (pending.type === 'publish') {
        const res = await actions.publishNow.mutateAsync(pending.ref)
        toast.success(res.external_url ? `게시했다 — ${res.external_url}` : '게시했다')
      } else {
        await actions.unschedule.mutateAsync(pending.ref)
        toast.success(pending.ref.kind === 'redeem' ? '예약을 내리고 초안으로 되돌렸다' : '예약을 내리고 검토 대기로 되돌렸다')
      }
      setPending(null)
    } catch (e) {
      toast.error(errorText(e))
      opts.onError?.(e)
      setPending(null)
    } finally {
      setBusy(false)
    }
  }

  const schedule = async (ref: PostRef, at: string) => {
    try {
      await actions.schedule.mutateAsync({ ref, at })
      toast.success('예약했다')
      return true
    } catch (e) {
      toast.error(errorText(e))
      opts.onError?.(e)
      return false
    }
  }

  const dialog = (
    <ConfirmDialog
      open={!!pending}
      onOpenChange={(o) => !o && !busy && setPending(null)}
      title={pending?.type === 'publish' ? `지금 ${pending.where}에 게시할까?` : '예약을 내릴까?'}
      description={
        pending?.type === 'publish'
          ? '예약 시각을 기다리지 않고 바로 올린다. 이미지 글은 브라우저를 거쳐 1~2분 걸린다.'
          : pending?.ref.kind === 'redeem'
            ? '초안으로 되돌린다. 글은 그대로 남는다.'
            : '검토 대기로 되돌린다. 다시 승인하면 새 슬롯을 받는다.'
      }
      confirmLabel={pending?.type === 'publish' ? '지금 게시' : '내리기'}
      confirmVariant={pending?.type === 'publish' ? 'primary' : 'destructive'}
      loading={busy}
      onConfirm={confirm}
    />
  )

  return {
    askPublish: (ref: PostRef, where: string) => {
      if (!isPublishing(ref)) setPending({ type: 'publish', ref, where })
    },
    /** True while a publish-now for this post is waiting on the platform. */
    isPublishing,
    askUnschedule: (ref: PostRef, where: string) => setPending({ type: 'unschedule', ref, where }),
    schedule,
    dialog,
  }
}

// ── the slot input ─────────────────────────────────────────────────────────

/** A slot picker and a button: the browser's local time in, UTC ISO out
 *  (fromLocalInput) — the only way a slot leaves the console. */
export function SlotForm({
  initial,
  label,
  submitLabel,
  onSubmit,
  onCancel,
  extra,
}: {
  /** UTC ISO, or a local "2026-10-17T09:00" value. */
  initial?: string | null
  label: string
  submitLabel: string
  onSubmit: (iso: string) => Promise<unknown> | void
  onCancel?: () => void
  extra?: ReactNode
}) {
  const [value, setValue] = useState(() =>
    initial && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(initial) ? initial : toLocalInput(initial),
  )
  const [busy, setBusy] = useState(false)
  const iso = fromLocalInput(value)
  // The server refuses a time that is already past; saying so here, before the
  // button is pressed, saves the round trip and the toast.
  const tooEarly = pastSlotError(iso)
  return (
    <div className="flex flex-wrap items-center gap-2">
      <SlotPicker label={label} value={value} onChange={setValue} size="sm" />
      <Button
        size="sm"
        variant="primary"
        loading={busy}
        disabled={!iso || !!tooEarly}
        onClick={async () => {
          if (!iso) return
          setBusy(true)
          try {
            await onSubmit(iso)
          } finally {
            setBusy(false)
          }
        }}
      >
        {submitLabel}
      </Button>
      {extra}
      {onCancel && (
        <Button size="sm" variant="ghost" onClick={onCancel}>
          취소
        </Button>
      )}
      {tooEarly && (
        <p role="alert" className="basis-full text-meta text-status-failed">
          {tooEarly}
        </p>
      )}
    </div>
  )
}

// ── writing a post ─────────────────────────────────────────────────────────

/** "글 쓰기": a desk post or a campaign draft, picked at the top. The forms
 *  live in DeskPostForm and DraftForm. */
export function WritePostDialog({
  open,
  onClose,
  initial = 'redeem',
  desk,
  campaign,
}: {
  open: boolean
  onClose: () => void
  initial?: Source
  desk: (done: () => void) => ReactNode
  campaign: (done: () => void) => ReactNode
}) {
  const [kind, setKind] = useState<Source>(initial)
  return (
    <Dialog open={open} onClose={onClose} title="글 쓰기" size="lg">
      <div className="max-h-[70vh] space-y-4 overflow-y-auto pb-2">
        <Segmented<Source>
          label="어느 쪽 글인가"
          value={kind}
          onChange={setKind}
          options={[
            { value: 'redeem', label: '데스크 글' },
            { value: 'campaign', label: '캠페인 글' },
          ]}
        />
        <p className="text-meta text-muted-foreground">
          {kind === 'redeem'
            ? '리딤 데스크가 들고 있는 홍보글. 초안으로 저장한 뒤 일정에서 예약한다.'
            : '캠페인 검토함에 올라가는 글. 승인하면 캠페인 페이스에 맞춰 슬롯을 받는다.'}
        </p>
        {kind === 'redeem' ? desk(onClose) : campaign(onClose)}
      </div>
    </Dialog>
  )
}
